import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTemplates } from "../hooks/useTemplates";
import type { Template } from "../types/api";

interface TemplateListProps {
  onSelect: (template: Template) => void;
  selectedTemplateId?: string;
}

const TemplateList: React.FC<TemplateListProps> = ({
  onSelect,
  selectedTemplateId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoadedTemplates, setAllLoadedTemplates] = useState<Template[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [videosPlaying, setVideosPlaying] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const playingVideos = useRef<Set<string>>(new Set());
  const MAX_CONCURRENT_VIDEOS = 20; // Allow all visible videos to play

  const { templates, loading, error } = useTemplates(currentPage, 20);

  // Check if container is scrollable
  const isScrollable = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return false;
    return container.scrollHeight > container.clientHeight;
  }, []);

  // Load next page
  const loadNextPage = useCallback(() => {
    if (templates && templates.pagination.has_next && !isLoadingMore && !loading) {
      setIsLoadingMore(true);
      setCurrentPage((prev) => prev + 1);
    }
  }, [templates, isLoadingMore, loading]);

  // Accumulate templates across pages
  useEffect(() => {
    if (templates && templates.templates.length > 0) {
      setAllLoadedTemplates((prev) => {
        // Get new templates that aren't already loaded
        const newTemplates = templates.templates.filter(
          (template) => !prev.some((t) => t.templateId === template.templateId)
        );
        if (newTemplates.length > 0) {
          return [...prev, ...newTemplates];
        }
        return prev;
      });
      setIsLoadingMore(false);
    }
  }, [templates]);

  // Play video if within concurrent limit
  const tryPlayVideo = useCallback((templateId: string, video: HTMLVideoElement) => {
    if (playingVideos.current.size >= MAX_CONCURRENT_VIDEOS) {
      return false; // At limit, can't play
    }

    if (video.readyState >= 2) {
      // Video has loaded enough data
      video.play().then(() => {
        playingVideos.current.add(templateId);
        setVideosPlaying(prev => new Set(prev).add(templateId));
      }).catch((err) => {
        console.warn(`Failed to autoplay video ${templateId}:`, err);
      });
      return true;
    }
    return false;
  }, []);

  // Pause video and remove from playing set
  const pauseVideo = useCallback((templateId: string, video: HTMLVideoElement) => {
    video.pause();
    video.currentTime = 0;
    playingVideos.current.delete(templateId);
  }, []);

  // Toggle play/pause
  const handleTogglePlayPause = useCallback((templateId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRefs.current.get(templateId);
    if (!video) return;

    if (video.paused) {
      video.play().then(() => {
        setVideosPlaying(prev => new Set(prev).add(templateId));
      }).catch((err) => {
        console.warn(`Play failed for video ${templateId}:`, err);
      });
    } else {
      video.pause();
      setVideosPlaying(prev => {
        const next = new Set(prev);
        next.delete(templateId);
        return next;
      });
    }
  }, []);

  // IntersectionObserver for each video - lazy load and autoplay
  useEffect(() => {
    if (allLoadedTemplates.length === 0) return;

    const observers: IntersectionObserver[] = [];

    allLoadedTemplates.forEach((template) => {
      const video = videoRefs.current.get(template.templateId);
      if (!video) return;

      const handleIntersection = (entries: IntersectionObserverEntry[]) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          // Video entered viewport
          // Set src and preload if not already set
          if (!video.src) {
            video.src = template.videoUrl;
            video.preload = "auto";
          }
          // Try to autoplay muted if under limit
          tryPlayVideo(template.templateId, video);
        } else {
          // Video left viewport - pause and reset
          pauseVideo(template.templateId, video);
          setVideosPlaying(prev => {
            const next = new Set(prev);
            next.delete(template.templateId);
            return next;
          });
        }
      };

      const observer = new IntersectionObserver(
        handleIntersection,
        {
          root: scrollContainerRef.current,
          rootMargin: "50px", // Start loading 50px before entering viewport
          threshold: 0.1,
        }
      );

      observer.observe(video);
      observers.push(observer);

      // Manually check if video is already visible on mount
      // IntersectionObserver may not fire immediately for already-visible elements
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Check if element is currently intersecting
          const checkIntersecting = () => {
            const rect = video.getBoundingClientRect();
            const container = scrollContainerRef.current;
            if (!container) return;

            const containerRect = container.getBoundingClientRect();
            const isIntersecting =
              rect.top < containerRect.bottom + 50 &&
              rect.bottom > containerRect.top - 50 &&
              rect.left < containerRect.right &&
              rect.right > containerRect.left;

            if (isIntersecting) {
              // Video is already visible - set src and play
              if (!video.src) {
                video.src = template.videoUrl;
                video.preload = "auto";
              }
              // Try to play if under limit
              tryPlayVideo(template.templateId, video);
            }
          };

          // Check immediately
          checkIntersecting();

          // Also check after a small delay as fallback (in case DOM isn't fully ready)
          setTimeout(checkIntersecting, 100);
        });
      });
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [allLoadedTemplates, tryPlayVideo, pauseVideo]);

  // Prefill until scrollable (or done) - bootstrap on mount/resize
  useEffect(() => {
    if (!templates || loading || isLoadingMore) return;
    if (!templates.pagination.has_next) return;

    const checkAndPrefill = async () => {
      // Wait for DOM to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (isScrollable()) return; // Already scrollable, done
      if (!templates.pagination.has_next) return; // No more data

      const maxPrefetches = 3;
      let prefetches = 0;

      while (prefetches < maxPrefetches) {
        if (isScrollable()) break;
        if (!templates.pagination.has_next) break;
        if (isLoadingMore || loading) break;

        loadNextPage();
        prefetches++;

        // Wait for next page to load
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    };

    checkAndPrefill();
  }, [templates, allLoadedTemplates.length, isScrollable, loadNextPage, isLoadingMore, loading]);

  // Also check on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isScrollable()) return;
      if (!templates?.pagination.has_next) return;

      // Small delay to let resize complete
      setTimeout(() => {
        if (!isScrollable() && templates?.pagination.has_next && !isLoadingMore && !loading) {
          loadNextPage();
        }
      }, 200);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [templates?.pagination, allLoadedTemplates.length, isLoadingMore, loading, isScrollable, loadNextPage]);

  // Sentinel + IntersectionObserver - load next page when sentinel enters view
  useEffect(() => {
    if (!templates) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && templates.pagination.has_next && !isLoadingMore && !loading) {
          loadNextPage();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: "100px", // Start loading slightly before sentinel is visible
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [templates, isLoadingMore, loading, loadNextPage]);

  // Cleanup: pause all videos on unmount
  useEffect(() => {
    const videos = videoRefs.current;
    const playing = playingVideos.current;
    
    return () => {
      videos.forEach((video, templateId) => {
        pauseVideo(templateId, video);
      });
      playing.clear();
    };
  }, [pauseVideo]);

  // Loading state
  if (loading && !templates) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-white/60">Loading templates...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/30">
          <svg
            className="w-6 h-6 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-white mb-2">Failed to Load Templates</h3>
        <p className="text-white/60 mb-4">{error}</p>
      </div>
    );
  }

  // Empty state
  if (allLoadedTemplates.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/20">
          <svg
            className="w-6 h-6 text-white/60"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-white mb-2">No Templates Available</h3>
        <p className="text-white/60">Templates will appear here once available.</p>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef}>
      {/* Templates Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {allLoadedTemplates.map((template) => (
          <div
            key={template.templateId}
            className={`bg-black/50 backdrop-blur-sm rounded-lg border-2 border-white overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group relative ${
              selectedTemplateId === template.templateId
                ? "ring-2 ring-blue-500/50"
                : ""
            }`}
          >
            {/* Video Preview */}
            <div className="aspect-square bg-gray-900 relative overflow-hidden">
              <video
                ref={(el) => {
                  if (el) {
                    videoRefs.current.set(template.templateId, el);
                  } else {
                    videoRefs.current.delete(template.templateId);
                  }
                }}
                data-src={template.videoUrl}
                className="w-full h-full object-cover"
                muted
                loop
                playsInline
                preload="none"
              />
              
              {/* Control Buttons Overlay */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center items-center gap-2">
                {/* Play/Pause Button */}
                <button
                  onClick={(e) => handleTogglePlayPause(template.templateId, e)}
                  className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/40 text-white rounded-full flex items-center justify-center hover:bg-white/20 hover:border-white/60 transition-all duration-200"
                  aria-label={videosPlaying.has(template.templateId) ? "Pause video" : "Play video"}
                >
                  {videosPlaying.has(template.templateId) ? (
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 ml-0.5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>
                
                {/* Recreate Button */}
                <button
                  onClick={() => onSelect(template)}
                  className="px-6 py-2 bg-white/10 backdrop-blur-sm border border-white/40 text-white font-medium rounded-full hover:bg-white/20 hover:border-white/60 transition-all duration-200"
                >
                  Recreate
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sentinel - triggers IntersectionObserver */}
      <div ref={sentinelRef} className="h-1 w-full" />

      {/* Loading More Indicator */}
      {isLoadingMore && templates && templates.pagination.has_next && (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-sm text-white/60">Loading more templates...</span>
        </div>
      )}

      {/* Fallback button - show if no scroll but has more */}
      {(() => {
        const scrollable = isScrollable();
        return (
          !isLoadingMore &&
          !loading &&
          templates &&
          templates.pagination.has_next &&
          !scrollable &&
          allLoadedTemplates.length > 0 && (
            <div className="text-center py-4">
              <button
                onClick={loadNextPage}
                className="px-4 py-2 border border-white/20 text-white/80 font-medium rounded-lg hover:bg-white/10 hover:border-white/40 transition-all text-sm"
              >
                Load More
              </button>
            </div>
          )
        );
      })()}

      {/* End of list indicator */}
      {!isLoadingMore &&
        templates &&
        !templates.pagination.has_next &&
        allLoadedTemplates.length > 0 && (
          <div className="text-center py-4">
            <p className="text-xs text-white/40">You've reached the end of templates</p>
          </div>
        )}
    </div>
  );
};

export default TemplateList;
